// src/store/configStore.ts
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { configApi } from '@/api/endpoints/config';
import toast from 'react-hot-toast';

interface Priority {
  _id: string;
  name: string;
  color: string;
  order: number;
  isActive: boolean;
  createdAt?: string;
}

interface Status {
  _id: string;
  name: string;
  color: string;
  followUpRequired: boolean;
  order: number;
  isActive: boolean;
  createdAt?: string;
}

interface TaskTemplate {
  _id: string;
  name: string;
  subTasks: string[];
  defaultPriority: string;
  defaultEstimatedTime: number;
  isActive: boolean;
  createdAt?: string;
}

interface Preferences {
  _id?: string;
  userId?: string;
  dateFormat: string;
  timeUnit: string;
  country: string;
  taxColumns: {
    column1: string;
    column2: string;
    column3: string;
  };
  showQuotationTaskWise: boolean;
  invoiceTerms: string;
  businessDetails?: {
    companyName?: string;
    address?: string;
    phone?: string;
    email?: string;
    gst?: string;
    logo?: string;
  };
}

interface ConfigState {
  priorities: Priority[];
  statuses: Status[];
  taskTemplates: TaskTemplate[];
  preferences: Preferences;
  loading: boolean;

  // Actions
  fetchPriorities: () => Promise<void>;
  fetchStatuses: () => Promise<void>;
  fetchTaskTemplates: () => Promise<void>;
  fetchPreferences: () => Promise<void>;
  
  // Priority actions
  createPriority: (data: { name: string; color: string; order?: number }) => Promise<void>;
  updatePriority: (id: string, updates: Partial<Priority>) => Promise<void>;
  deletePriority: (id: string) => Promise<void>;
  
  // Status actions
  createStatus: (data: { name: string; color: string; followUpRequired?: boolean; order?: number }) => Promise<void>;
  updateStatus: (id: string, updates: Partial<Status>) => Promise<void>;
  deleteStatus: (id: string) => Promise<void>;
  
  // Task Template actions
  createTaskTemplate: (data: { name: string; subTasks: string[]; defaultPriority?: string; defaultEstimatedTime?: number }) => Promise<void>;
  updateTaskTemplate: (id: string, updates: Partial<TaskTemplate>) => Promise<void>;
  deleteTaskTemplate: (id: string) => Promise<void>;
  
  // Preferences actions
  updatePreferences: (preferences: Partial<Preferences>) => Promise<void>;
  updateBusinessDetails: (details: any) => Promise<void>;
  uploadLogo: (file: File) => Promise<void>;
  resetPreferences: () => void;
}

export const useConfigStore = create<ConfigState>()(
  devtools(
    persist(
      (set, get) => ({
        priorities: [],
        statuses: [],
        taskTemplates: [],
        preferences: {
          dateFormat: 'DD/MM/YYYY',
          timeUnit: 'Minutes',
          country: 'India',
          taxColumns: {
            column1: 'CGST',
            column2: 'SGST',
            column3: 'IGST',
          },
          showQuotationTaskWise: true,
          invoiceTerms: '',
        },
        loading: false,

     fetchPriorities: async () => {
  set({ loading: true });
  try {
    const response = await configApi.getPriorities();
    console.log('Raw priorities response:', response);
    
    // The actual priorities array is at response.data.data.data
    const priorities = response.data?.data?.data || [];
    
    console.log('Extracted priorities:', priorities);
    
    set({ 
      priorities: Array.isArray(priorities) ? priorities : [], 
      loading: false 
    });
  } catch (error: any) {
    console.error('Failed to fetch priorities:', error);
    set({ loading: false, priorities: [] });
    toast.error(error.response?.data?.message || 'Failed to fetch priorities');
  }
},

        fetchStatuses: async () => {
          set({ loading: true });
          try {
            const response = await configApi.getStatuses();
            const data = response.data?.data || [];
            set({ 
              statuses: Array.isArray(data) ? data : [], 
              loading: false 
            });
          } catch (error: any) {
            console.error('Failed to fetch statuses:', error);
            set({ loading: false, statuses: [] });
            toast.error(error.response?.data?.message || 'Failed to fetch statuses');
          }
        },

        fetchTaskTemplates: async () => {
  set({ loading: true });
  try {
    const response = await configApi.getTaskTemplates();
    
    // Based on your console log, the structure is response.data.data
    const templates = response.data?.data?.data || [];
        
    set({ 
      taskTemplates: templates, 
      loading: false 
    });
  } catch (error: any) {
    console.error('Failed to fetch task templates:', error);
    set({ loading: false, taskTemplates: [] });
    toast.error(error.response?.data?.message || 'Failed to fetch task templates');
  }
},


        fetchPreferences: async () => {
          set({ loading: true });
          try {
            const response = await configApi.getPreferences();
            console.log('response: ', response);
            set({ preferences: response.data?.data?.data || get().preferences, loading: false });
          } catch (error: any) {
            console.error('Failed to fetch preferences:', error);
            set({ loading: false });
            toast.error(error.response?.data?.message || 'Failed to fetch preferences');
          }
        },

        createPriority: async (data) => {
          try {
            const response = await configApi.createPriority(data);
            const newPriority = response.data?.data;
            if (newPriority) {
              set((state) => ({
                priorities: [...state.priorities, newPriority],
              }));
              toast.success('Priority created successfully');
            }
          } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to create priority');
            throw error;
          }
        },

        updatePriority: async (id, updates) => {
          try {
            const response = await configApi.updatePriority(id, updates);
            const updatedPriority = response.data?.data;
            if (updatedPriority) {
              set((state) => ({
                priorities: state.priorities.map(p => 
                  p._id === id ? updatedPriority : p
                ),
              }));
              toast.success('Priority updated successfully');
            }
          } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to update priority');
            throw error;
          }
        },

        deletePriority: async (id) => {
          try {
            await configApi.deletePriority(id);
            set((state) => ({
              priorities: state.priorities.filter(p => p._id !== id),
            }));
            toast.success('Priority deleted successfully');
          } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to delete priority');
          }
        },

        createStatus: async (data) => {
          try {
            const response = await configApi.createStatus(data);
            const newStatus = response.data?.data;
            if (newStatus) {
              set((state) => ({
                statuses: [...state.statuses, newStatus],
              }));
              toast.success('Status created successfully');
            }
          } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to create status');
            throw error;
          }
        },

        updateStatus: async (id, updates) => {
          try {
            const response = await configApi.updateStatus(id, updates);
            const updatedStatus = response.data?.data;
            if (updatedStatus) {
              set((state) => ({
                statuses: state.statuses.map(s => 
                  s._id === id ? updatedStatus : s
                ),
              }));
              toast.success('Status updated successfully');
            }
          } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to update status');
            throw error;
          }
        },

        deleteStatus: async (id) => {
          try {
            await configApi.deleteStatus(id);
            set((state) => ({
              statuses: state.statuses.filter(s => s._id !== id),
            }));
            toast.success('Status deleted successfully');
          } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to delete status');
          }
        },

        createTaskTemplate: async (data) => {
  try {
    console.log('Creating task template with data:', data);
    const response = await configApi.createTaskTemplate(data);
    console.log('Create response:', response);
    
    const newTemplate = response.data?.data;
    
    if (newTemplate) {
      set((state) => ({
        taskTemplates: [...state.taskTemplates, newTemplate],
      }));
      toast.success('Task template created successfully');
    } else {
      // If no data returned, refetch the list
      await get().fetchTaskTemplates();
      toast.success('Task template created successfully');
    }
  } catch (error: any) {
    console.error('Error creating task template:', error);
    toast.error(error.response?.data?.message || 'Failed to create task template');
    throw error;
  }
},

        updateTaskTemplate: async (id, updates) => {
          try {
            const response = await configApi.updateTaskTemplate(id, updates);
            const updatedTemplate = response.data?.data;
            if (updatedTemplate) {
              set((state) => ({
                taskTemplates: state.taskTemplates.map(t => 
                  t._id === id ? updatedTemplate : t
                ),
              }));
              toast.success('Task template updated successfully');
            }
          } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to update task template');
            throw error;
          }
        },

        deleteTaskTemplate: async (id) => {
          try {
            await configApi.deleteTaskTemplate(id);
            set((state) => ({
              taskTemplates: state.taskTemplates.filter(t => t._id !== id),
            }));
            toast.success('Task template deleted successfully');
          } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to delete task template');
          }
        },

        updatePreferences: async (preferences) => {
          try {
            const response = await configApi.updatePreferences(preferences);
            set({ preferences: response.data?.data || preferences });
            toast.success('Preferences saved successfully');
          } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to save preferences');
            throw error;
          }
        },

        updateBusinessDetails: async (details) => {
          try {
            const response = await configApi.updateBusinessDetails(details);
            set((state) => ({
              preferences: {
                ...state.preferences,
                businessDetails: response.data?.data?.businessDetails || details
              }
            }));
            toast.success('Business details updated successfully');
          } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to update business details');
            throw error;
          }
        },

        uploadLogo: async (file) => {
          try {
            const response = await configApi.uploadLogo(file);
            const logoUrl = response.data?.logoUrl;
            if (logoUrl) {
              set((state) => ({
                preferences: {
                  ...state.preferences,
                  businessDetails: {
                    ...state.preferences.businessDetails,
                    logo: logoUrl
                  }
                }
              }));
              toast.success('Logo uploaded successfully');
            }
          } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to upload logo');
            throw error;
          }
        },

        resetPreferences: () => {
          const defaultPreferences = {
            dateFormat: 'DD/MM/YYYY',
            timeUnit: 'Minutes',
            country: 'India',
            taxColumns: {
              column1: 'CGST',
              column2: 'SGST',
              column3: 'IGST',
            },
            showQuotationTaskWise: true,
            invoiceTerms: '',
          };
          set({ preferences: defaultPreferences });
          toast.success('Preferences reset to default');
        },
      }),
      {
        name: 'config-store',
        partialize: (state) => ({ preferences: state.preferences }),
      }
    )
  )
);